import { useState } from 'react';
import { Info, X } from 'lucide-react';

/**
 * Le « i » qui explique un bloc ou un champ.
 *
 * Un écran de réglages est limpide pour qui l'a construit et opaque pour qui
 * le découvre. « Descripteur de relevé », « clôture calendaire », « part
 * d'expédition » : chacun de ces intitulés est juste, et aucun ne dit ce
 * qu'il faut y mettre ni ce qui se passera si on le change.
 *
 * L'explication est donc là, mais repliée. Dépliée par défaut, elle
 * doublerait la longueur de l'écran pour un président qui l'a lue une fois et
 * n'y revient jamais ; absente, elle laisserait le suivant deviner.
 *
 * Deux tailles : « bloc » pour un titre de section, « champ » pour une ligne
 * de formulaire — le second se glisse à côté d'un libellé sans le déformer.
 */
export default function Aide({
  titre,
  children,
  taille = 'champ',
}: {
  /** Ce que l'aide explique, repris dans l'étiquette d'accessibilité. */
  titre: string;
  children: React.ReactNode;
  taille?: 'bloc' | 'champ';
}) {
  const [ouverte, setOuverte] = useState(false);

  const cote = taille === 'bloc' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  return (
    <>
      <button
        type="button"
        aria-label={ouverte ? `Masquer l'aide : ${titre}` : `À quoi sert ${titre} ?`}
        aria-expanded={ouverte}
        onClick={(e) => {
          // Le « i » vit souvent dans un en-tête qui bascule au clic : sans
          // cela, demander l'aide replierait la section qu'on veut lire.
          e.stopPropagation();
          setOuverte((o) => !o);
        }}
        className={`shrink-0 inline-grid place-items-center rounded-full transition-colors ${
          taille === 'bloc' ? 'w-6 h-6' : 'w-5 h-5'
        } ${
          ouverte
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
            : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20'
        }`}
      >
        <Info className={cote} />
      </button>

      {ouverte && (
        <div
          // L'aide occupe toute la largeur sous son titre : les conteneurs
          // qui l'accueillent sont des flex, et « basis-full » la renvoie à la
          // ligne plutôt que de l'écraser à côté du libellé.
          className="basis-full mt-2 rounded-lg border border-primary-200 bg-primary-50/60 dark:border-primary-800 dark:bg-primary-900/15 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2 [&_strong]:text-gray-900 dark:[&_strong]:text-gray-100">
              {children}
            </div>
            <button
              type="button"
              aria-label="Masquer l'aide"
              onClick={(e) => {
                e.stopPropagation();
                setOuverte(false);
              }}
              className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
